import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import ChatAxios from '../../services/api/chatAxios';

interface DemoRequestFormData {
  customer_name: string;
  customer_email: string;
}

interface DemoRequestResponse {
  success: boolean;
  message: string;
  data?: any;
}

const extractSessionId = (payload: any): string | null => {
  if (!payload) return null;
  return (
    payload.session_id ||
    payload.sessionId ||
    payload.id ||
    payload?.data?.session_id ||
    payload?.data?.sessionId ||
    payload?.data?.id ||
    null
  );
};

const sendDemoRequest = async (data: DemoRequestFormData): Promise<DemoRequestResponse> => {
  const roomKey = process.env.REACT_APP_CHAT_ROOM_KEY || 'lottoreco_chat';

  console.log('Demo Request Debug:', {
    roomKey,
    data
  });

  try {
    const userInfoToStore = {
      customer_name: data.customer_name,
      customer_email: data.customer_email,
      room_key: roomKey
    };
    localStorage.setItem('chat_user_info', JSON.stringify(userInfoToStore));

    const sessionResponse = await ChatAxios.post('/chat/sessions', {
      ...data,
      room_key: roomKey,
      request_type: 'demo_request'
    });

    const sessionId = extractSessionId(sessionResponse.data);
    if (!sessionId) {
      throw new Error('Failed to create session');
    }

    const messageResponse = await ChatAxios.post(`/sessions/${sessionId}/messages`, {
      message: `${data.customer_name} at ${data.customer_email} is requesting a demo.`
    });

    return {
      success: true,
      message: 'Demo request sent successfully',
      data: {
        session_id: sessionId,
        message_sent: messageResponse.data
      }
    };
  } catch (error: any) {
    console.error('Demo Request Error:', error);

    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please refresh the page and try again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. Please contact support.');
    } else if (error.response?.status === 404) {
      throw new Error('Service temporarily unavailable. Please try again later.');
    } else if (error.response?.status === 429) {
      throw new Error('Too many requests. Please wait a moment and try again.');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else if (error.code === 'NETWORK_ERROR' || !navigator.onLine) {
      throw new Error('Network error. Please check your connection and try again.');
    } else {
      throw new Error(error.response?.data?.message || error.message || 'Failed to send demo request. Please try again.');
    }
  }
};

export const useDemoRequest = () => {
  const [isOpen, setIsOpen] = useState(false);

  const demoRequestMutation = useMutation({
    mutationFn: sendDemoRequest,
    retry: (failureCount, error: any) => {
      if (error.message?.includes('Network error') ||
          error.message?.includes('Server error') ||
          error.response?.status >= 500) {
        return failureCount < 2;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    onSuccess: () => {
      toast.success('Demo request sent! We\'ll send you a demo link shortly.');
      setIsOpen(false);
    },
    onError: (error: any) => {
      const errorMessage = error.message || 'Failed to send demo request. Please try again.';
      toast.error(errorMessage);
      console.error('Demo request error:', error);
    },
  });

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSubmit = (data: DemoRequestFormData) => {
    demoRequestMutation.mutate(data);
  };

  return {
    isOpen,
    isPending: demoRequestMutation.isPending,
    isRetrying: demoRequestMutation.isPending && demoRequestMutation.failureCount > 0,
    handleOpen,
    handleClose,
    handleSubmit,
  };
};
